import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Simple cache for GET requests
const apiCache = new Map();
const CACHE_TTL = 5000; // 5 seconds cache

const getCached = (key) => {
  const cached = apiCache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.data;
  }
  apiCache.delete(key);
  return null;
};

const setCache = (key, data) => {
  apiCache.set(key, { data, timestamp: Date.now() });
};

const api = axios.create({
  baseURL: API,
});

// Add token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('dragon_tv_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Cached GET helper
const cachedGet = (url, useCache = true) => {
  if (useCache) {
    const cached = getCached(url);
    if (cached) {
      return Promise.resolve(cached);
    }
  }
  return api.get(url).then(response => {
    if (useCache) {
      setCache(url, response);
    }
    return response;
  });
};

export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  updateProfile: (data) => api.patch('/auth/profile', data),
  updateDecorations: (data) => api.patch('/auth/decorations', data),
  updateSettings: (settings) => api.patch('/auth/settings', settings),
  equipItem: (itemId) => api.post(`/auth/equip/${itemId}`),
  unequipItem: (itemId) => api.post(`/auth/unequip/${itemId}`),
  changePassword: (data) => api.post('/auth/change-password', data),
  deleteAccount: () => api.delete('/auth/account'),
  markWatched: (episodeId) => api.post('/watch-episode', { episode_id: episodeId }),
  unmarkWatched: (episodeId) => api.post('/unwatch-episode', { episode_id: episodeId }),
  getWatchedEpisodes: () => api.get('/watched-episodes'),
};

export const userAPI = {
  getUsers: () => api.get('/users'),
  updateRole: (userId, role) => api.patch(`/users/${userId}/role`, { role }),
  banUser: (userId, reason = '', durationHours = null) => api.post('/users/ban', { user_id: userId, reason, duration_hours: durationHours }),
  unbanUser: (userId) => api.post(`/users/${userId}/unban`),
  getBannedUsers: () => api.get('/users/banned'),
  giveZeni: (userId, amount, currency = 'zeni') => api.post(`/users/${userId}/give-zeni`, { amount, currency }),
};

export const seriesAPI = {
  create: (data) => api.post('/series', data),
  getAll: () => cachedGet('/series'),
  getById: (id) => cachedGet(`/series/${id}`),
  update: (id, data) => { apiCache.clear(); return api.patch(`/series/${id}`, data); },
  delete: (id) => { apiCache.clear(); return api.delete(`/series/${id}`); },
};

export const seasonAPI = {
  create: (data) => { apiCache.clear(); return api.post('/seasons', data); },
  getAll: () => cachedGet('/seasons'),
  getById: (id) => cachedGet(`/seasons/${id}`),
  update: (id, data) => { apiCache.clear(); return api.patch(`/seasons/${id}`, data); },
  delete: (id) => { apiCache.clear(); return api.delete(`/seasons/${id}`); },
  deleteAllEpisodes: (id) => { apiCache.clear(); return api.delete(`/seasons/${id}/episodes`); },
};

export const episodeAPI = {
  
  importDiscord: (data) => api.post('/episodes/import-discord', data),
  getDiscordImportStatus: (jobId) => api.get(`/episodes/import-discord/${jobId}`),
  create: (data) => { apiCache.clear(); return api.post('/episodes', data); },
  getAll: (seasonId) => cachedGet(`/episodes${seasonId ? `?season_id=${seasonId}` : ''}`),
  getById: (id) => cachedGet(`/episodes/${id}`),
  update: (id, data) => { apiCache.clear(); return api.patch(`/episodes/${id}`, data); },
  delete: (id) => { apiCache.clear(); return api.delete(`/episodes/${id}`); },
  reorder: (id, newEpisodeNumber) => { apiCache.clear(); return api.patch(`/episodes/${id}/reorder`, { episode_id: id, new_episode_number: newEpisodeNumber }); },
  // Comments
  getComments: (id) => api.get(`/episodes/${id}/comments`),
  addComment: (id, message, parentId = null) => api.post(`/episodes/${id}/comments`, { message, parent_id: parentId }),
};

export const commentAPI = {
  like: (id) => api.post(`/comments/${id}/like`),
  delete: (id) => api.delete(`/comments/${id}`),
  pin: (id) => api.post(`/comments/${id}/pin`),
};

export const messageAPI = {
  getMessages: (room, limit = 100) => api.get('/messages', { params: { room, limit } }),
  deleteMessage: (messageId) => api.delete(`/messages/${messageId}`),
  editMessage: (messageId, message) => api.put(`/messages/${messageId}`, { message }),
  uploadImage: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/chat/upload-image', fd);
  },
  searchMessages: (query, room = 'global', limit = 50) => api.get('/messages/search', { params: { q: query, room, limit } }),
  addReaction: (messageId, reaction, room = 'global') => api.post(`/messages/${messageId}/reactions`, null, { params: { reaction, room } }),
  removeReaction: (messageId, reaction, room = 'global') => api.delete(`/messages/${messageId}/reactions`, { params: { reaction, room } }),
};

export const leaderboardAPI = {
  getLeaderboard: (limit = 50) => api.get('/leaderboard', { params: { limit } }),
  getTransformations: (limit = 10) => api.get('/transformations', { params: { limit } }),
};

export const friendAPI = {
  getFriends: () => api.get('/friends'),
  sendRequest: (userId) => api.post('/friends/request', { user_id: userId }),
  acceptRequest: (userId) => api.post('/friends/accept', { user_id: userId }),
  rejectRequest: (userId) => api.post('/friends/reject', { user_id: userId }),
  removeFriend: (userId) => api.post('/friends/remove', { user_id: userId }),
};

export const blockAPI = {
  blockUser: (userId) => api.post('/block', { user_id: userId }),
  unblockUser: (userId) => api.post('/unblock', { user_id: userId }),
  getBlocked: () => api.get('/blocked'),
};

export const dmAPI = {
  getConversation: (userId, limit = 30, before = null) => {
    const params = { limit };
    if (before) params.before = before;
    return api.get(`/dm/${userId}`, { params });
  },
  getConversations: () => api.get('/dm/conversations'),
  send: (userId, message, messageType = 'text', metadata = null) => api.post(`/dm/${userId}`, { message, message_type: messageType, metadata }),
  sendMessage: (userId, message, messageType = 'text', metadata = null) => api.post(`/dm/${userId}`, { message, message_type: messageType, metadata }),
  getUnreadCount: () => api.get('/dm/unread/count'),
  search: (query, userId, limit = 50) => api.get('/dm/search', { params: { q: query, user_id: userId, limit } }),
  deleteMessage: (messageId) => api.delete(`/dm/messages/${messageId}`),
  addReaction: (messageId, reaction) => api.patch(`/dm/messages/${messageId}/reactions`, null, { params: { reaction } }),
  removeReaction: (messageId, reaction) => api.delete(`/dm/messages/${messageId}/reactions`, { params: { reaction } }),
};

export const groupAPI = {
  create: (name, memberIds) => api.post('/groups', { name, member_ids: memberIds }),
  getAll: () => api.get('/groups'),
  getById: (id) => api.get(`/groups/${id}`),
  update: (id, data) => api.patch(`/groups/${id}`, data),
  delete: (id) => api.delete(`/groups/${id}`),
  leave: (id) => api.post(`/groups/${id}/leave`),
  getMessages: (id) => api.get(`/groups/${id}/messages`),
  sendMessage: (id, message, messageType = 'text') => api.post(`/groups/${id}/messages`, { message, message_type: messageType }),
};

export const profileAPI = {
  getPublicProfile: (userId) => api.get(`/users/${userId}/profile`),
};

export const videoProgressAPI = {
  save: (episodeId, progress, duration) => api.post('/video-progress', { episode_id: episodeId, progress, duration }),
  get: (episodeId) => api.get(`/video-progress/${episodeId}`),
  getAll: () => api.get('/video-progress'),
  clear: (episodeId) => api.delete(`/video-progress/${episodeId}`),
  getWatchNext: () => api.get('/watch-next'),
};

export const notificationAPI = {
  getSettings: () => api.get('/notification-settings'),
  updateSettings: (data) => api.patch('/notification-settings', data),
  savePushSubscription: (subscription) => api.post('/push-subscription', { subscription }),
  deletePushSubscription: () => api.delete('/push-subscription'),
  getVapidKey: () => api.get('/vapid-public-key'),
  testPush: () => api.post('/test-push'),
};

export const searchAPI = {
  search: (query, limit = 20, includeUsers = false) => api.get('/search', { params: { q: query, limit, include_users: includeUsers } }),
};

export const emoteAPI = {
  getAll: () => api.get('/emotes'),
  create: (name, imageData) => api.post('/emotes', { name, image_data: imageData }),
  delete: (emoteId) => api.delete(`/emotes/${emoteId}`),
};

export const roomAPI = {
  getAll: () => api.get('/rooms'),
  getBySlug: (slug) => api.get(`/rooms/${slug}`),
  getMessages: (slug, limit = 100) => api.get(`/rooms/${slug}/messages`, { params: { limit } }),
  create: (data) => api.post('/rooms', data),
  update: (slug, data) => api.patch(`/rooms/${slug}`, data),
  delete: (slug) => api.delete(`/rooms/${slug}`),
  // Pinned messages
  getPinnedMessage: (slug) => api.get(`/rooms/${slug}/pinned`),
  pinMessage: (slug, messageId) => api.post(`/rooms/${slug}/pin/${messageId}`),
  unpinMessage: (slug) => api.delete(`/rooms/${slug}/pin`),
  // Channel muting
  getMutedChannels: () => api.get('/channels/muted'),
  muteChannel: (slug) => api.post(`/channels/mute/${slug}`),
  unmuteChannel: (slug) => api.delete(`/channels/mute/${slug}`),
  // Analytics (admin only)
  getAnalytics: () => api.get('/rooms/analytics'),
};

export const videoAPI = {
  upload: (file, title, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    if (title) formData.append('title', title);
    
    return api.post('/videos/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0, // No timeout for uploads
      onUploadProgress: (progressEvent) => {
        if (onProgress) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        }
      }
    });
  },
  getStatus: (videoId) => api.get(`/videos/status/${videoId}`),
  getUploaded: () => api.get('/videos/uploaded'),
  getReady: () => api.get('/videos/ready'),
  delete: (videoId) => api.delete(`/videos/${videoId}`),
  // Thumbnail generation
  generateThumbnails: () => api.post('/videos/thumbnails/generate-all'),
  regenerateThumbnails: () => api.post('/videos/thumbnails/regenerate-all'),
};

export const proxyAPI = {
  // Cache management (admin only)
  getCacheStats: () => api.get('/proxy/cache/stats'),
  clearCache: () => api.delete('/proxy/cache'),
  deleteCacheFile: (filename) => api.delete(`/proxy/cache/${encodeURIComponent(filename)}`),
  // Mirror management
  getMirrorStatus: () => api.get('/proxy/mirror-status'),
  getEpisodeMirrorStatus: (episodeId) => api.get(`/proxy/mirror/status/${episodeId}`),
  retryAllMirrors: () => api.post('/proxy/retry-all-mirrors'),
  retryMirror: (episodeId) => api.post(`/proxy/retry-mirror/${episodeId}`),
  startMirrorDownload: (episodeId) => api.post(`/proxy/mirror/start/${episodeId}`),
  getMirrorStats: () => api.get('/proxy/mirror-stats'),
  deleteMirror: (episodeId) => api.delete(`/proxy/mirror/${episodeId}`),
  // Get proxied Discord CDN URL
  getProxiedUrl: (discordUrl) => {
    if (!discordUrl) return null;
    const isDiscord = /cdn\.discordapp\.com|media\.discordapp\.net|discord\.com\/attachments/.test(discordUrl);
    if (!isDiscord) return discordUrl;
    return `${API}/proxy/discord?url=${encodeURIComponent(discordUrl)}`;
  },
  // Check if URL is valid
  checkUrl: (url) => api.get('/proxy/check', { params: { url } }),
  // Get mirror URL
  getMirrorUrl: (mirrorPath) => mirrorPath ? `${API}${mirrorPath}` : null,
};

export const dragonBallAPI = {
  getCollection: () => api.get('/dragon-balls'),
  getSpawnState: (path) => api.get('/dragon-balls/spawn-state', { params: { path } }),
  triggerSpawn: (path) => api.post('/dragon-balls/trigger-spawn', null, { params: { path } }),
  radarScan: (path, zoom) => api.get('/dragon-radar/scan', { params: { path, zoom } }),
  radarAchievements: () => api.get('/dragon-radar/achievements'),
  collect: (ballNumber) => api.post(`/dragon-balls/collect/${ballNumber}`),
  getWishes: () => api.get('/dragon-balls/wishes'),
  makeWish: (wishId) => api.post('/dragon-balls/wish', { wish_id: wishId }),
  getAccessiblePages: () => api.get('/dragon-balls/accessible-pages'),
};

export const adsAPI = {
  serve: (placement) => api.get(`/ads/serve?placement=${placement}`),
  submitRequest: (formData, onUploadProgress) => api.post('/ads/requests', formData, { onUploadProgress }),
  adminList: () => api.get('/ads/admin'),
  adminCreate: (formData, onUploadProgress) => api.post('/ads/admin', formData, { onUploadProgress }),
  adminUpdate: (adId, formData) => api.put(`/ads/admin/${adId}`, formData),
  adminDelete: (adId) => api.delete(`/ads/admin/${adId}`),
  adminRequests: () => api.get('/ads/admin/requests'),
  adminApprove: (requestId, body) => api.post(`/ads/admin/requests/${requestId}/approve`, body),
  adminReject: (requestId) => api.delete(`/ads/admin/requests/${requestId}`),
};

export const shopAPI = {
  getItems: () => api.get('/shop'),
  getBalance: () => api.get('/shop/balance'),
  getInventory: () => api.get('/shop/inventory'),
  getAchievements: () => api.get('/shop/achievements'),
  purchase: (itemId) => api.post('/shop/purchase', { item_id: itemId }),
  equip: (itemId) => api.post('/shop/equip', { item_id: itemId }),
  unequip: (itemId) => api.post('/shop/unequip', { item_id: itemId }),
  adminGrantZeni: (userId, amount) => api.post(`/shop/admin/grant-zeni?user_id=${userId}&amount=${amount}`),
  getDragonRadarStatus: () => api.get('/shop/dragon-radar-status'),
  getConsumables: () => api.get('/shop/consumables'),
  useConsumable: (itemId) => api.post('/shop/use-consumable', { item_id: itemId }),
  getTimeRingStatus: () => api.get('/shop/time-ring-status'),
  getAdFreeStatus: () => api.get('/shop/ad-free-status'),
  // Admin functions - custom items
  getAdminItems: () => api.get('/shop/admin/items'),
  createItem: (item) => api.post('/shop/admin/items', item),
  updateItem: (itemId, updates) => api.put(`/shop/admin/items/${itemId}`, updates),
  deleteItem: (itemId) => api.delete(`/shop/admin/items/${itemId}`),
  uploadImage: (formData) => api.post('/shop/admin/upload-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getAuraPositions: () => api.get('/shop/admin/aura-positions'),
  updateAuraPosition: (data) => api.put('/shop/admin/aura-position', data),
  getThemes: () => api.get('/shop/admin/themes'),
  createTheme: (formData) => api.post('/shop/admin/themes', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // Admin functions - hardcoded items
  getHardcodedItems: () => api.get('/shop/admin/hardcoded-items'),
  updateHardcodedItem: (itemId, updates) => api.put(`/shop/admin/hardcoded-items/${itemId}`, updates),
  removeHardcodedOverride: (itemId) => api.delete(`/shop/admin/hardcoded-items/${itemId}/override`),
  hideHardcodedItem: (itemId) => api.post(`/shop/admin/hardcoded-items/${itemId}/hide`),
  unhideHardcodedItem: (itemId) => api.post(`/shop/admin/hardcoded-items/${itemId}/unhide`),
};

export const paymentsAPI = {
  getPackages: () => api.get('/payments/packages'),
  getConfig: () => api.get('/payments/config'),
  createPaymentIntent: (packageId) => api.post('/payments/create-payment-intent', { package_id: packageId }),
  confirmPayment: (transactionId, paymentIntentId) => api.post('/payments/confirm-payment', { 
    transaction_id: transactionId, 
    payment_intent_id: paymentIntentId 
  }),
  getPaymentStatus: (transactionId) => api.get(`/payments/status/${transactionId}`),
  getHistory: () => api.get('/payments/history'),
};

export const callAPI = {
  // Voice/Video Calls
  startCall: (callType, targetType, targetId) => api.post('/calls/start', { call_type: callType, target_type: targetType, target_id: targetId }),
  joinCall: (callId) => api.post(`/calls/${callId}/join`),
  endCall: (callId) => api.post(`/calls/${callId}/end`),
  getCall: (callId) => api.get(`/calls/${callId}`),
  checkIncoming: () => api.get('/calls/incoming/check'),
  leaveCall: (callId) => api.post(`/calls/${callId}/leave`),
  
  
  // HTTP-based signaling (WebSocket fallback)
  sendSignal: (callId, type, data) =>
    api.post(`/calls/${callId}/signal`, { type, data }),

  getSignals: (callId) =>
    api.get(`/calls/${callId}/signals`),

  // Confirm that signaling messages were processed.
  // Prevents messages from being lost before the browser handles them.
  ackSignals: (callId, signalIds) =>
    api.post(`/calls/${callId}/signals/ack`, {
      signal_ids: signalIds
    }),

  // Watch Together (integrated with calls)
  startWatchTogether: (callId, episodeData) => 
    api.post(`/calls/${callId}/watch-together/start`, episodeData),
  syncWatchTogether: (callId, currentTime, isPlaying) => 
    api.post(`/calls/${callId}/watch-together/sync`, { current_time: currentTime, is_playing: isPlaying }),
  endWatchTogether: (callId) => 
    api.post(`/calls/${callId}/watch-together/end`),
};

// WebSocket URLs for calls
export const getCallWebSocketUrl = (callId, userId, username) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  console.log('[Call] Backend URL from env:', backendUrl);
  const wsBase = backendUrl.replace('https://', 'wss://').replace('http://', 'ws://');
  const fullUrl = `${wsBase}/api/calls/ws/${callId}?user_id=${userId}&username=${encodeURIComponent(username)}`;
  console.log('[Call] WebSocket URL:', fullUrl);
  return fullUrl;
};


// Episode Names API - Official Dragon Ball episode titles
export const episodeNamesAPI = {
  // Get list of all series
  getSeries: () => api.get('/episode-names/series'),
  // Get sagas for a series (version: 'edited' or 'uncut')
  getSagas: (series, version = 'edited') => api.get('/episode-names/sagas', { params: { series, version } }),
  // Get all episodes for a saga (version: 'edited' or 'uncut')
  getEpisodes: (series, saga, version = 'edited') => api.get('/episode-names/episodes', { params: { series, saga, version } }),
  // Look up a specific episode name (version: 'edited' or 'uncut')
  lookup: (series, saga, episodeNumber, version = 'edited') => api.get('/episode-names/lookup', { 
    params: { series, saga, episode_number: episodeNumber, version } 
  }),
  // Get all episode data (version: 'edited' or 'uncut')
  getAll: (version = 'edited') => api.get('/episode-names/all', { params: { version } }),
  // Auto-fix episodes for a series - reorganizes episodes into correct sagas with official names
  autoFix: (seriesName, version = 'edited') => { 
    apiCache.clear(); 
    return api.post('/episode-names/auto-fix', null, { params: { series_name: seriesName, version } }); 
  },
};

// User Reports API (for reporting users, account issues, bugs)
export const userReportsAPI = {
  // Submit a new report
  create: (reportData) => api.post('/user-reports', reportData),
  // Get all reports (owner only)
  getAll: (status, reportType) => api.get('/user-reports', { params: { status, report_type: reportType } }),
  // Get my submitted reports
  getMyReports: () => api.get('/user-reports/my-reports'),
  // Get report statistics (owner only)
  getStats: () => api.get('/user-reports/stats'),
  // Update report status (owner only)
  update: (reportId, updateData) => api.patch(`/user-reports/${reportId}`, updateData),
  // Delete a report (owner only)
  delete: (reportId) => api.delete(`/user-reports/${reportId}`),
};

// Discord Link API
export const discordLinkAPI = {
  // Get pending link request for current user
  getPending: () => api.get('/discord-link/pending'),
  // Confirm a link request
  confirm: (requestId) => api.post(`/discord-link/confirm/${requestId}`),
  // Deny a link request
  deny: (requestId) => api.post(`/discord-link/deny/${requestId}`),
  // Get link status
  getStatus: () => api.get('/discord-link/status'),
  // Unlink Discord account
  unlink: () => api.delete('/discord-link/unlink'),
  // Claim booster reward
  claimBoosterReward: () => api.post('/discord-link/claim-booster-reward'),
};

export default api;
